import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { paymentService } from '../services';

const DELIVERY_FEE = 50;

export default function OrderStep1() {
  const { token, user } = useContext(AuthContext);
  const { fetchCart } = useCart();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cart_meds')) || []; }
    catch { return []; }
  });

  const [phase, setPhase] = useState('checkout'); // 'checkout' | 'placing' | 'paying'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get('/addresses/', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const list = res.data.data || res.data.addresses || [];
        setAddresses(list);
        const def = list.find(a => a.is_default);
        if (def) setSelectedAddressId(def.id);
      })
      .catch(() => setError('Failed to load addresses. Please try again.'));
  }, [token]);

  const handleQtyChange = (id, delta) => {
    const updated = cart
      .map(item => item.id === id ? { ...item, qty: item.qty + delta } : item)
      .filter(item => item.qty > 0);
    setCart(updated);
    localStorage.setItem('cart_meds', JSON.stringify(updated));
  };

  const uploadPrescription = async () => {
    if (!prescriptionFile) return;
    const formData = new FormData();
    formData.append('image', prescriptionFile);
    await api.post('/prescription/upload/', formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    });
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      setError('Please select a delivery address.');
      return;
    }
    setLoading(true);
    setError('');
    setPhase('placing');

    try {
      // Upload prescription (optional, non-fatal)
      try { await uploadPrescription(); } catch { /* non-fatal */ }

      // POST /checkout/ uses the backend cart and returns the real order totals
      const checkoutRes = await api.post('/checkout/', { address_id: selectedAddressId }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const placedOrder = checkoutRes.data.order;
      setOrder(placedOrder);
      setPhase('paying');

      // Open Razorpay immediately after checkout
      await paymentService.processPayment(
        'medicine_order',
        placedOrder.id,
        { name: user?.username || '', email: user?.email || '', mobile: user?.mobile || '' },
        (statusData) => {
          // Payment captured — clear local cart and go to success page
          localStorage.removeItem('cart_meds');
          fetchCart();
          navigate(
            `/order-success?order_id=${placedOrder.order_id}&total=${placedOrder.total_amount}&payment_id=${statusData.razorpay_order_id || 'PAID'}&service_type=medicine`
          );
        },
        (err) => {
          setError(err.message || 'Payment failed. Your order is saved — retry from Order History.');
          setPhase('checkout');
          setLoading(false);
        }
      );
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Something went wrong. Please try again.');
      setPhase('checkout');
      setLoading(false);
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + parseFloat(item.offer_price || 0) * item.qty, 0);
  const estimatedTotal = subtotal + DELIVERY_FEE;
  const selectedAddress = addresses.find(a => a.id === selectedAddressId);
  const isProcessing = phase === 'placing' || phase === 'paying';

  // ── Empty cart screen ────────────────────────────────────────────────────
  if (cart.length === 0) {
    return (
      <>
        <Header />
        <div style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🛒</div>
          <h2 style={{ color: '#333', marginBottom: 8 }}>Your cart is empty</h2>
          <p style={{ color: '#666', marginBottom: 24 }}>Add medicines before checking out.</p>
          <button
            onClick={() => navigate('/Medicines')}
            style={{ padding: '12px 28px', backgroundColor: '#104a5f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer' }}
          >
            Browse Medicines
          </button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div style={{ minHeight: '80vh', backgroundColor: '#f5f7fa', padding: '24px 16px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>

          {/* Page title */}
          <h2 style={{ color: '#104a5f', marginBottom: 6 }}>Checkout</h2>
          <p style={{ color: '#888', fontSize: 14, marginBottom: 24 }}>Review your items, choose delivery address, and pay.</p>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>

            {/* ── Left column ─────────────────────────────────────────── */}
            <div style={{ flex: '1 1 340px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Address */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ margin: 0, color: '#104a5f', fontSize: 17 }}>📍 Delivery Address</h3>
                  <button
                    onClick={() => navigate('/add-address')}
                    disabled={isProcessing}
                    style={{ fontSize: 13, color: '#104a5f', background: 'none', border: '1px solid #104a5f', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                  >
                    + Add New
                  </button>
                </div>

                {addresses.length === 0 ? (
                  <p style={{ color: '#888', fontSize: 14 }}>No saved addresses. Add one to continue.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {addresses.map(addr => (
                      <label
                        key={addr.id}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12,
                          border: selectedAddressId === addr.id ? '2px solid #104a5f' : '1px solid #e0e0e0',
                          borderRadius: 10, cursor: 'pointer',
                          backgroundColor: selectedAddressId === addr.id ? '#f0f7fa' : '#fff',
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="radio"
                          name="address"
                          value={addr.id}
                          checked={selectedAddressId === addr.id}
                          onChange={() => setSelectedAddressId(addr.id)}
                          disabled={isProcessing}
                          style={{ marginTop: 3, accentColor: '#104a5f' }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>
                            {addr.name}
                            {addr.is_default && (
                              <span style={{ marginLeft: 8, fontSize: 11, color: '#104a5f', background: '#e8f4f8', padding: '2px 6px', borderRadius: 4 }}>Default</span>
                            )}
                          </div>
                          <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                            {[addr.address_line_1, addr.address_line_2, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}
                          </div>
                          {addr.phone && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>📞 {addr.phone}</div>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Prescription upload */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <h3 style={{ margin: '0 0 8px', color: '#104a5f', fontSize: 17 }}>
                  📋 Prescription{' '}
                  <span style={{ fontWeight: 400, fontSize: 13, color: '#888' }}>(optional)</span>
                </h3>
                <p style={{ fontSize: 13, color: '#666', margin: '0 0 12px' }}>
                  Upload if any medicine in your order requires one.
                </p>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', border: '1.5px dashed #104a5f',
                  borderRadius: 8, cursor: 'pointer', background: '#f8fbfd',
                }}>
                  <span style={{ fontSize: 20 }}>📁</span>
                  <span style={{ fontSize: 13, color: '#104a5f' }}>
                    {prescriptionFile ? prescriptionFile.name : 'Click to upload (JPG, PNG, PDF)'}
                  </span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    disabled={isProcessing}
                    onChange={e => setPrescriptionFile(e.target.files[0] || null)}
                    style={{ display: 'none' }}
                  />
                </label>
                {prescriptionFile && (
                  <button
                    onClick={() => setPrescriptionFile(null)}
                    style={{ marginTop: 8, fontSize: 12, color: '#e74c3c', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    ✕ Remove
                  </button>
                )}
              </div>
            </div>

            {/* ── Right column ─────────────────────────────────────────── */}
            <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Cart items */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <h3 style={{ margin: '0 0 16px', color: '#104a5f', fontSize: 17 }}>🛍️ Order Items ({cart.length})</h3>
                {cart.map((item, idx) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      paddingBottom: 14, marginBottom: idx < cart.length - 1 ? 14 : 0,
                      borderBottom: idx < cart.length - 1 ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{item.name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>₹{parseFloat(item.offer_price).toFixed(2)} each</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        onClick={() => handleQtyChange(item.id, -1)}
                        disabled={isProcessing}
                        style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid #104a5f', background: '#fff', color: '#104a5f', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}
                      >−</button>
                      <span style={{ fontWeight: 600, minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                      <button
                        onClick={() => handleQtyChange(item.id, 1)}
                        disabled={isProcessing}
                        style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: '#104a5f', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 16 }}
                      >+</button>
                    </div>
                    <div style={{ minWidth: 64, textAlign: 'right', fontSize: 14, fontWeight: 600, color: '#333', marginLeft: 12 }}>
                      ₹{(parseFloat(item.offer_price) * item.qty).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Price summary */}
              <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                <h3 style={{ margin: '0 0 16px', color: '#104a5f', fontSize: 17 }}>🧾 Price Summary</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14, color: '#555' }}>
                  <span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14, color: '#555' }}>
                  <span>Delivery Fee</span><span>₹{DELIVERY_FEE}.00</span>
                </div>
                <div style={{ borderTop: '1px dashed #ddd', paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 17, fontWeight: 700, color: '#104a5f' }}>
                  <span>Estimated Total</span><span>₹{estimatedTotal.toFixed(2)}</span>
                </div>
                <p style={{ fontSize: 11, color: '#bbb', marginTop: 8, marginBottom: 0 }}>
                  * Final amount confirmed by server when order is placed.
                </p>
              </div>

              {/* Selected address preview */}
              {selectedAddress && (
                <div style={{ backgroundColor: '#eaf6fb', borderRadius: 10, padding: 14, fontSize: 13, color: '#555', border: '1px solid #b3ddf0' }}>
                  <strong style={{ color: '#104a5f' }}>Delivering to:</strong><br />
                  {selectedAddress.name} — {[selectedAddress.address_line_1, selectedAddress.city, selectedAddress.pincode].filter(Boolean).join(', ')}
                </div>
              )}

              {/* Processing status */}
              {isProcessing && (
                <div style={{ padding: 14, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 10, fontSize: 14, color: '#7d6608', textAlign: 'center' }}>
                  {phase === 'placing' ? '⏳ Placing your order...' : '💳 Opening payment gateway...'}
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{ padding: 12, background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 8, color: '#cf1322', fontSize: 14 }}>
                  ⚠️ {error}
                </div>
              )}

              {/* CTA */}
              <button
                onClick={handlePlaceOrder}
                disabled={isProcessing || !selectedAddressId}
                style={{
                  width: '100%', padding: '15px 0', fontSize: 17, fontWeight: 700,
                  backgroundColor: isProcessing || !selectedAddressId ? '#a0c4cf' : '#104a5f',
                  color: '#fff', border: 'none', borderRadius: 10,
                  cursor: isProcessing || !selectedAddressId ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 14px rgba(16,74,95,0.2)',
                  transition: 'background-color 0.2s',
                }}
              >
                {isProcessing ? '⏳ Processing...' : '🔒 Place Order & Pay'}
              </button>

              <p style={{ textAlign: 'center', fontSize: 12, color: '#aaa', margin: 0 }}>
                Secured by Razorpay · UPI, Cards & Net Banking accepted
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
